/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  const ensureAvadaThemeFont = () => {
    if (document.getElementById("avada-theme-font-override")) {
      return;
    }

    const style = document.createElement("style");
    style.id = "avada-theme-font-override";
    style.textContent = `
      #Avada-FAQ_Widget .Avada-Launcher__ButtonCustom,
      #Avada-FAQ_Widget .Avada-Launcher__ButtonCustom *,
      #Avada-FAQ_Widget .Avada-Faqs_Button,
      #Avada-FAQ_Widget .Avada-Faqs_Button *,
      #Avada_Faq_Popup-Container,
      #Avada_Faq_Popup-Container * {
        font-family: var(--text-font-family) !important;
        font-style: var(--text-font-style) !important;
      }

      #Avada-FAQ_Widget .Avada-Launcher__ButtonCustom,
      #Avada-FAQ_Widget .Avada-Faqs_Button,
      #Avada-FAQ_Widget .Avada-Faqs_Button-Body,
      #Avada-FAQ_Widget .Avada-Faqs_Button-Content,
      #Avada-FAQ_Widget .Avada-Faqs_Button-Icon,
      #Avada-FAQ_Widget .Avada-Faqs_Button-Icon *,
      #Avada-FAQ_Widget .Avada-Launcher__ButtonCustom::before,
      #Avada-FAQ_Widget .Avada-Launcher__ButtonCustom::after {
        border-radius: 0 !important;
      }

      #pandectes-banner,
      #pandectes-banner *,
      .pandectes-banner,
      .pandectes-banner *,
      .pandectes-modal,
      .pandectes-modal *,
      .pandectes-consent-popup,
      .pandectes-consent-popup *,
      [id*="pandectes"][class*="button"],
      [id*="pandectes"] button,
      [class*="pandectes"] button,
      [class*="pandectes"] a,
      [class*="pandectes"] input,
      [class*="pandectes"] select,
      [class*="pandectes"] textarea,
      .inner.has-header,
      .inner.has-header *,
      .inner.has-header.outer,
      .inner.has-header.outer *,
      .inner.has-header .header,
      .inner.has-header .body,
      .inner.has-header .actions,
      .inner.has-header .group,
      .inner.has-header .links,
      .inner.has-header .logo,
      .inner.has-header button,
      .inner.has-header button.filled,
      .inner.has-header button.allow,
      .inner.has-header button.deny {
        border-radius: 0 !important;
      }

      .inner.has-header,
      .inner.has-header.outer {
        border-radius: 0 !important;
        overflow: hidden !important;
      }
    `;

    document.head.appendChild(style);
  };

  const helloText = "Hej Hej 👋";
  const helpText = "Behöver du hjälp?";
  const contactText = "Kontakta oss";
  const onlineText = "Vi är online";
  const resolvedConversationText =
    "Den här konversationen har lösts. Skicka gärna ett meddelande för att öppna den igen - vi finns här för att hjälpa!";
  const orderTrackingTitleText = "Orderstatus";
  const orderTrackingSubtitleText = "Spåra dina beställningar";
  const orderNumberText = "Ordernummer";
  const trackingNumberText = "Spårningsnummer";
  const orderNumberPlaceholderText = "t.ex. 1001";
  const emailAddressText = "E-postadress";
  const phoneNumberText = "Telefonnummer";
  const trackButtonText = "Spåra";
  const chatNowText = "Chatta nu";
  const searchPlaceholderText = "Sök för hjälp";
  const namePlaceholderText = "Namn";
  const messagePlaceholderText = "Skriv ditt meddelande";
  const transferredToHumanText = "Din fr\u00e5ga har vidarebefordrats. En m\u00e4nsklig agent hj\u00e4lper dig snart!";
  const sendTranscriptText = "Skicka utdrag till";
  const sendButtonText = "Skicka";
  const assistantIntroText =
    "Hej 👋 Jag är Chatty AI-agenten och hjälper dig att hitta det du söker. Hur kan jag hjälpa dig?";
  const chattyAgentGreetingPattern =
    /^Hello\s*👋\s*I'm Chatty AI agent, here to help you find what you're looking for\.\s*How can I help you\?$/i;
  const followUpPattern =
    /^Hi\s+(.+?),\s+thanks for providing your contact information!\s+How can I assist you today\?$/i;
  const followUpTemplate = (name) =>
    "Hej " +
    name +
    ", tack for att du angav dina kontaktuppgifter! Hur kan jag hjalpa dig idag?";
  const startChatText = "Starta chatten";
  const todayText = "Idag";
  const emailRequiredText = "E-post är obligatoriskt.";
  const orderNumberRequiredText = "Ordernummer är obligatoriskt.";
  const emailInvalidText = "Felaktig e-post";
  const phoneInvalidText = "Felaktigt telefonnummer";
  const trackingNumberRequiredText = "Spårningsnummer är obligatoriskt";
  const noMatchingArticleText = "Ingen matchning hittades";
  const categoriesText = "Kategorier";
  const viewAllText = "Se allt";
  const articleSingularText = "Ämne";
  const articlePluralText = "Ämnen";
  const helpTextLabel = "Hjälp";
  const rootSelectors = ["#Avada-FAQ_Widget", "#Avada_Faq_Popup-Container"];

  const isTargetText = (text) => {
    const trimmed = text.trim();
    return trimmed === "Hello" || trimmed === "Hello 👋" || trimmed === "Hello👋";
  };

  const isHelpText = (text) =>
    text.trim() === "How can we help you?" || text.trim() === "How can we help?";

  const updateAvadaFaqText = () => {
    ensureAvadaThemeFont();

    const root = rootSelectors
      .map((selector) => document.querySelector(selector))
      .find(Boolean);

    if (!root) {
      return;
    }

    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let node = walker.nextNode();
    while (node) {
      const value = node.nodeValue || "";
      const trimmed = value.trim();

      if (isTargetText(trimmed)) {
        node.nodeValue = helloText;
      } else if (followUpPattern.test(trimmed)) {
        const match = trimmed.match(followUpPattern);
        node.nodeValue = match ? followUpTemplate(match[1]) : trimmed;
      } else if (chattyAgentGreetingPattern.test(trimmed)) {
        node.nodeValue = assistantIntroText;
      } else if (
        (trimmed.includes("I'm Chatty AI assistant") || trimmed.includes("I'm Chatty AI agent")) &&
        trimmed.includes("How can I help you?")
      ) {
        node.nodeValue = assistantIntroText;
      } else if (isHelpText(trimmed)) {
        node.nodeValue = helpText;
      } else if (trimmed === "Contact us") {
        node.nodeValue = contactText;
      } else if (trimmed === "We are online") {
        node.nodeValue = onlineText;
      } else if (
        trimmed ===
        "The conversation has been resolved. Feel free to send a message to reopen it - we're here to help!"
      ) {
        node.nodeValue = resolvedConversationText;
      } else if (trimmed === "Order tracking") {
        node.nodeValue = orderTrackingTitleText;
      } else if (trimmed === "Track your orders") {
        node.nodeValue = orderTrackingSubtitleText;
      } else if (trimmed === "Order number") {
        node.nodeValue = orderNumberText;
      } else if (trimmed === "Tracking number") {
        node.nodeValue = trackingNumberText;
      } else if (trimmed === "Email address") {
        node.nodeValue = emailAddressText;
      } else if (trimmed === "Phone number") {
        node.nodeValue = phoneNumberText;
      } else if (trimmed === "Track") {
        node.nodeValue = trackButtonText;
      } else if (value.includes("Chat now")) {
        const updated = value.replace(/Chat now/g, "").trim();
        node.nodeValue = updated || chatNowText;
      } else if (trimmed === "Start chat") {
        node.nodeValue = startChatText;
      } else if (trimmed === "Today") {
        node.nodeValue = todayText;
      } else if (trimmed === "Email is required") {
        node.nodeValue = emailRequiredText;
      } else if (trimmed === "Order number is required") {
        node.nodeValue = orderNumberRequiredText;
      } else if (trimmed === "Email is invalid") {
        node.nodeValue = emailInvalidText;
      } else if (trimmed === "Phone number is invalid") {
        node.nodeValue = phoneInvalidText;
      } else if (trimmed === "Tracking number is required") {
        node.nodeValue = trackingNumberRequiredText;
      } else if (trimmed === "No matching article found") {
        node.nodeValue = noMatchingArticleText;
      } else if (trimmed === "Categories") {
        node.nodeValue = categoriesText;
      } else if (trimmed === "View all") {
        node.nodeValue = viewAllText;
      } else if (trimmed === "Article") {
        node.nodeValue = articleSingularText;
      } else if (trimmed === "Articles") {
        node.nodeValue = articlePluralText;
      } else if (/^\d+\s+Articles$/.test(trimmed)) {
        const count = parseInt(trimmed.split(" ")[0], 10);
        const label = count === 1 ? articleSingularText : articlePluralText;
        node.nodeValue = `${count} ${label}`;
      } else if (/^\d+\s+Article$/.test(trimmed)) {
        const count = parseInt(trimmed.split(" ")[0], 10);
        const label = count === 1 ? articleSingularText : articlePluralText;
        node.nodeValue = `${count} ${label}`;
      } else if (trimmed === "Help") {
        node.nodeValue = helpTextLabel;
      } else if (trimmed === "Your question has been transferred. A human agent will help you soon!") {
        node.nodeValue = transferredToHumanText;
      } else if (trimmed === "Send transcript to") {
        node.nodeValue = sendTranscriptText;
      } else if (trimmed === "Send") {
        node.nodeValue = sendButtonText;
      }

      node = walker.nextNode();
    }

    const inputs = root.querySelectorAll('input[placeholder="Search for help"]');
    for (const input of inputs) {
      input.setAttribute("placeholder", searchPlaceholderText);
    }

    const nameInputs = root.querySelectorAll('input[placeholder="Name"]');
    for (const input of nameInputs) {
      input.setAttribute("placeholder", namePlaceholderText);
    }

    const orderNumberInputs = root.querySelectorAll('input[placeholder="e.g. 1001"]');
    for (const input of orderNumberInputs) {
      input.setAttribute("placeholder", orderNumberPlaceholderText);
    }

    const messageInputs = root.querySelectorAll('textarea[placeholder="Type your message"],input[placeholder="Type your message"]');
    for (const input of messageInputs) {
      input.setAttribute("placeholder", messagePlaceholderText);
    }

    const poweredByNodes = root.querySelectorAll("span,div,p,small");
    for (const el of poweredByNodes) {
      if ((el.textContent || "").trim() === "Powered by Chatty") {
        el.textContent = "";
      }
    }
    const resolvedEls = root.querySelectorAll(".Avada-message__resolve");
    for (const el of resolvedEls) {
      if ((el.textContent || "").trim() !== resolvedConversationText) {
        el.textContent = resolvedConversationText;
      }
    }
    const transferredEls = root.querySelectorAll(".Avada-Faq_Message-Wrapper");
    for (const el of transferredEls) {
      if ((el.textContent || "").trim() === "Your question has been transferred. A human agent will help you soon!") {
        el.textContent = transferredToHumanText;
      }
    }
  };
  const observer = new MutationObserver(updateAvadaFaqText);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", updateAvadaFaqText);
  } else {
    updateAvadaFaqText();
  }
})();

(() => {
  const privacyHeadingPattern = /Vi respekterar din integritet/i;
  const acceptPattern = /Acceptera alla/i;
  const necessaryPattern = /Endast n[öo]dv[aä]ndiga/i;

  const forceSquareCorners = (root) => {
    if (!root || root.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    root.style.setProperty("border-radius", "0", "important");

    root.querySelectorAll("button, a, input, select, textarea, div, span").forEach((el) => {
      const text = (el.textContent || "").trim();
      if (
        acceptPattern.test(text) ||
        necessaryPattern.test(text) ||
        el.querySelector("button") ||
        el === root
      ) {
        el.style.setProperty("border-radius", "0", "important");
      }
    });
  };

  const findPrivacyPopup = () => {
    const elements = document.querySelectorAll("div, section, aside");

    for (const el of elements) {
      const text = (el.textContent || "").trim();
      if (!privacyHeadingPattern.test(text)) {
        continue;
      }

      const buttons = Array.from(el.querySelectorAll("button"));
      const hasConsentButtons = buttons.some((button) =>
        acceptPattern.test((button.textContent || "").trim()) ||
        necessaryPattern.test((button.textContent || "").trim())
      );

      if (!hasConsentButtons) {
        continue;
      }

      return el;
    }

    return null;
  };

  const applyPrivacyPopupRadiusOverride = () => {
    const popup = findPrivacyPopup();
    if (!popup) {
      return;
    }

    forceSquareCorners(popup);
    popup.querySelectorAll("*").forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.setProperty("border-radius", "0", "important");
      }
    });
  };

  const observer = new MutationObserver(applyPrivacyPopupRadiusOverride);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", applyPrivacyPopupRadiusOverride);
  } else {
    applyPrivacyPopupRadiusOverride();
  }
})();

(() => {
  const toggleAvadaButton = () => {
    const button = document.querySelector(
      "#Avada-FAQ_Widget .Avada-Launcher__ButtonCustom"
    );
    const buttonWrapper = document.querySelector(
      "#Avada-FAQ_Widget .Avada-Faqs_Button"
    );
    const status = document.querySelector(
      "#Avada-FAQ_Widget .Avada-Faqs_Button-Status"
    );
    const body = document.querySelector(
      "#Avada-FAQ_Widget .Avada-Faqs_Body"
    );
    const miniCart = document.getElementById("mini-cart");
    if (!miniCart) {
      return;
    }

    const isOpen = miniCart.hasAttribute("open");
    if (button) {
      button.style.display = isOpen ? "none" : "";
    }
    if (buttonWrapper) {
      buttonWrapper.style.display = isOpen ? "none" : "";
    }
    if (status) {
      status.style.display = isOpen ? "none" : "";
    }
    if (body) {
      body.style.display = isOpen ? "none" : "";
    }
  };

  const init = () => {
    const miniCart = document.getElementById("mini-cart");
    if (!miniCart) {
      return;
    }

    const observer = new MutationObserver(toggleAvadaButton);
    observer.observe(miniCart, { attributes: true, attributeFilter: ["open"] });
    toggleAvadaButton();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

(() => {
  const openNewsletterModal = () => {
    const tryOpen = () => {
      const modal = document.getElementById("newsletter-popup");
      if (!modal) {
        return false;
      }

      // Set both attribute + property so opening works even before/after CE upgrade.
      modal.setAttribute("open", "");
      modal.open = true;
      return true;
    };

    if (tryOpen()) {
      return;
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", tryOpen, { once: true });
    }

    if (window.customElements && typeof window.customElements.whenDefined === "function") {
      window.customElements.whenDefined("modal-content").then(() => {
        tryOpen();
      });
    }

    setTimeout(tryOpen, 50);
  };

  const bindFloatingButton = (button) => {
    if (!button || button.dataset.newsletterBound === "true") {
      return;
    }

    const handlePress = (event) => {
      if (event) {
        event.preventDefault();
      }
      openNewsletterModal();
    };

    button.type = "button";
    button.setAttribute("aria-controls", "newsletter-popup");
    button.setAttribute("aria-expanded", "false");
    button.addEventListener("click", handlePress);
    button.addEventListener("pointerup", handlePress);
    button.addEventListener("touchend", handlePress, { passive: false });
    button.dataset.newsletterBound = "true";
  };

  const createButton = () => {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("is", "toggle-button");
    button.className = "floating-newsletter-button";
    button.setAttribute("aria-controls", "newsletter-popup");
    button.setAttribute("aria-expanded", "false");
    button.textContent = "Få -5%";
    bindFloatingButton(button);
    return button;
  };

  const setup = () => {
    const existing = document.querySelector(".floating-newsletter-button");
    if (existing) {
      bindFloatingButton(existing);
      return;
    }

    const button = createButton();
    document.body.appendChild(button);

    const onScroll = () => {
      if (window.scrollY >= 600) {
        button.classList.add("is-visible");
      } else {
        button.classList.remove("is-visible");
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setup);
  } else {
    setup();
  }

  // iOS/Safari fallback: if the floating button exists/re-renders later, delegated handlers still open instantly.
  const delegatedOpen = (event) => {
    const trigger = event.target.closest(".floating-newsletter-button");
    if (!trigger) {
      return;
    }
    event.preventDefault();
    openNewsletterModal();
  };

  document.addEventListener("click", delegatedOpen);
  document.addEventListener("pointerup", delegatedOpen);
  document.addEventListener("touchend", delegatedOpen, { passive: false });
})();

(() => {
  const openNewsletterModal = () => {
    const tryOpen = () => {
      const modal = document.getElementById("newsletter-popup");
      if (!modal) {
        return false;
      }

      modal.setAttribute("open", "");
      modal.open = true;
      return true;
    };

    if (tryOpen()) {
      return;
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", tryOpen, { once: true });
    }

    if (window.customElements && typeof window.customElements.whenDefined === "function") {
      window.customElements.whenDefined("modal-content").then(() => {
        tryOpen();
      });
    }

    setTimeout(tryOpen, 50);
  };

  const handleClick = (event) => {
    const trigger = event.target.closest("[data-newsletter-trigger]");
    if (!trigger) {
      return;
    }

    event.preventDefault();
    openNewsletterModal();
  };

  const handleKeydown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    const trigger = event.target.closest("[data-newsletter-trigger]");
    if (!trigger) {
      return;
    }

    event.preventDefault();
    openNewsletterModal();
  };

  document.addEventListener("click", handleClick);
  document.addEventListener("keydown", handleKeydown);
})();

(function () {
  const maxVisible = 2;

  const applyReviewFold = (root) => {
    const widgets = root.matches && root.matches(".jdgm-rev-widg__body")
      ? [root]
      : root.querySelectorAll("#judgeme_product_reviews .jdgm-rev-widg__body");
    for (const widget of widgets) {
      const reviews = widget.querySelectorAll(".jdgm-rev");
      if (reviews.length <= maxVisible) {
        const existing = widget.querySelector(".jdgm-rev-widg__toggle");
        if (existing) {
          existing.remove();
        }
        continue;
      }

      const isExpanded = widget.dataset.jdgmExpanded === "true";
      reviews.forEach((review, index) => {
        review.style.display = !isExpanded && index >= maxVisible ? "none" : "";
      });

      let toggle = widget.querySelector(".jdgm-rev-widg__toggle");
      if (!toggle) {
        toggle = document.createElement("button");
        toggle.type = "button";
        toggle.className = "jdgm-rev-widg__toggle";
        toggle.addEventListener("click", () => {
          const nextState = widget.dataset.jdgmExpanded !== "true";
          widget.dataset.jdgmExpanded = nextState ? "true" : "false";
          applyReviewFold(document);
        });
        widget.appendChild(toggle);
      }
      toggle.textContent = isExpanded ? "VISA FÄRRE RECENSIONER" : "VISA FLER RECENSIONER";
    }
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          applyReviewFold(node);
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      applyReviewFold(document);
    });
  } else {
    applyReviewFold(document);
  }
})();

(() => {
  const toggleAccordion = (button) => {
    const accordion = button.closest("[data-accordion]");
    if (!accordion) {
      return;
    }

    const excerpt = accordion.querySelector("[data-accordion-excerpt]");
    const full = accordion.querySelector("[data-accordion-full]");
    if (!excerpt || !full) {
      return;
    }

    const isExpanded = button.getAttribute("aria-expanded") === "true";
    if (isExpanded) {
      full.hidden = true;
      excerpt.hidden = false;
      button.setAttribute("aria-expanded", "false");
      button.textContent = "L\u00e4s mer";
    } else {
      excerpt.hidden = true;
      full.hidden = false;
      button.setAttribute("aria-expanded", "true");
      button.textContent = "L\u00e4s mindre";
    }
  };

  const handleClick = (event) => {
    const button = event.target.closest("[data-accordion-toggle]");
    if (!button) {
      return;
    }

    toggleAccordion(button);
  };

  document.addEventListener("click", handleClick);
})();

(() => {
  const BLOG_PREFIX = "/blogs/bast-i-test/";
  const ARTICLE_SELECTOR = ".article__content .rte";
  const CARD_CLASS = "article-buy-card";
  const ARTICLE_PRODUCT_MAP = {
    "/blogs/bast-i-test/basta-resvaskan-2026-5-resvaskor-bast-i-test": [
      {
        heading: "1. American Tourister Airconic 77 cm – bäst i test",
        handles: ["airconic-american-tourister-77-cm-resvaska"]
      },
      {
        heading: "2. Cavalet Malibu 65 cm – mest prisvärd",
        handles: ["malibu-cavalet-65-cm-resvaska"]
      },
      {
        heading: "3. Travelite Air Base Trolley M – bästa lätta resväskan",
        handles: ["travelite-air-base-trolley-m-exp-expanderbar-resvaska"]
      },
      {
        heading: "4. Hedgren Yuno L EXP – bästa stora expanderbara resväskan",
        handles: ["yuno-l-exp"]
      },
      {
        heading: "5. Victorinox Airox Advanced Large Case – bästa premiumval",
        handles: ["victorinox-airox-advanced-large-case"]
      }
    ],
    "/blogs/bast-i-test/basta-kabinvaskan-2026-5-kabinvaskor-bast-i-test": [
      {
        heading: "1. American Tourister Airconic 55 cm – bäst i test",
        handles: [
          "airconic-american-tourister-55-cm-kabinvaska",
          "airconic-55cm-kabinbagage",
          "airconic-55cm"
        ]
      },
      {
        heading: "2. Cavalet Malibu 54 cm – mest prisvärd",
        handles: [
          "malibu-cavalet-54-cm-kabinvaska",
          "cavalet-malibu-kabinvaska-54cm",
          "malibu-54"
        ]
      },
      {
        heading: "3. Travelite Air Base Trolley S Slim – bäst för smalare mått",
        handles: ["travelite-air-base-trolley-s-slim-kabinvaska"]
      },
      {
        heading: "4. Airbox AS4 S – bästa mjuka kabinväskan",
        handles: ["as4-s-kabinvaska"]
      },
      {
        heading: "5. Victorinox Airox Global Hardside – bästa premiumval",
        handles: [
          "victorinox-airox-global-hardside-carry-on",
          "victorinox-airox-advanced-global-carry-on"
        ]
      }
    ],
    "/blogs/bast-i-test/basta-ryggsacken-2026-5-ryggsackar-bast-i-test": [
      {
        heading: "1. American Tourister Pacepro 15,6 tum – bäst i test",
        handles: [
          "pacepro-american-tourister-15-6-laptop-ryggsack",
          "pacepro-ryggsack-15-6"
        ]
      },
      {
        heading: "2. Hedgren Handle L 15,6 tum RFID – bäst för arbete och pendling",
        handles: ["hedgren-comby-handle-l-ryggsack"]
      },
      {
        heading: "3. Tracker Original Cabin Pack 30 L – bästa reseryggsäcken",
        handles: ["original-cabin-pack-30l-ryggsack"]
      },
      {
        heading: "4. Horizn Studios Gion Backpack Pro M – bästa lätta premiumryggsäcken",
        handles: ["gion-backpack-pro-m"]
      },
      {
        heading: "5. Victorinox Architecture Urban2 City Backpack – bästa exklusiva arbetsryggsäcken",
        handles: ["victorinox-architecture-urban2-city-backpack"]
      }
    ],
    "/blogs/bast-i-test/bast-i-test-duffelvaskor-2026-7-modeller-for-weekendresor-traning-och-semester": [
      {
        heading: "1. Travelite Kick Off Travel Bag L – bäst i test",
        handles: ["kick-off-travel-bag-l-duffelvaska"]
      },
      {
        heading: "2. Horizn Studios Shibuya Weekender – bästa weekendväskan",
        handles: [
          "shibuya-weekender",
          "horizn-studios-shibuya-weekender"
        ]
      },
      {
        heading: "3. American Tourister Urban Track S – bästa duffelväskan med ryggsäcksremmar",
        handles: ["urban-track-american-tourister-s-duffelvaska-ryggsack-158937"]
      },
      {
        heading: "4. Travelite Skaii Trolley – bästa duffelväskan med hjul",
        handles: ["travelite-skaii-trolley-duffelvaska"]
      },
      {
        heading: "5. Tracker Waterproof Duffel Bag 50 L – bästa slitstarka duffelväskan",
        handles: ["waterproof-duffel-bag-50-l-vaska"]
      },
      {
        heading: "6. Cavalet Cargo BPT L 65 CM – bästa rymliga duffelväskan",
        handles: ["cargo-bpt-l-cavalet-65-cm-duffelvaska"]
      },
      {
        heading: "7. Go Travel Classic Foldaway Travel Bag – bästa hopvikbara duffelväskan",
        handles: ["classic-foldaway-travel-bag"]
      }
    ],
    "/blogs/bast-i-test/bast-i-test-handvaskor-2026-7-modeller-for-olika-behov": [
      {
        heading: "1. Hedgren Tomoko – bäst i test",
        handles: ["tomoko-hedgren-handvaska"]
      },
      {
        heading: "2. Ulrika Design Pocket – bästa rymliga handväskan",
        handles: [
          "timeless-2-0-ulrika-handvaska",
          "recycled-2-0-ulrika-handvaska"
        ]
      },
      {
        heading: "3. Victorinox Victoria Signature Tote – bästa handväskan för jobbet",
        handles: ["victorinox-victoria-signature-tote"]
      },
      {
        heading: "4. Hedgren Softy – bästa lätta handväskan",
        handles: ["softy-hedgren-handvaska"]
      },
      {
        heading: "5. Saddler Estelle – bästa handväskan i skinn",
        handles: ["estelle-totevaska"]
      },
      {
        heading: "6. Ulrika Design Rhomb Kedja – bästa trendiga valet",
        handles: [
          "party-ulrika-vaska",
          "quilt-ulrika-axelvaska",
          "quilt-ulrika-axelremsvaska"
        ]
      },
      {
        heading: "7. Hedgren Eye – bästa kompakta handväskan",
        handles: [
          "eye-hedgren-crossbody",
          "eye-m-hedgren-crossbody"
        ]
      }
    ],
    "/blogs/bast-i-test/bast-i-test-datorvaskor-2026-7-modeller-for-arbete-pendling-och-resa": [
      {
        heading: "1. Horizn Studios Gion Briefcase – bäst i test",
        handles: ["gion-briefcase"]
      },
      {
        heading: "2. American Tourister At Work Nxt 3-Way Boarding Bag – bästa prisvärda datorväskan",
        handles: ["at-work-nxt-american-tourister-3-way-boarding-bag-15-6"]
      },
      {
        heading: "3. Saddler Taylor – bästa datorväskan i läder",
        handles: ["taylor-datorvaska"]
      },
      {
        heading: "4. Victorinox Werks Traveler 7.0 Deluxe Briefcase – bästa premiumvalet",
        handles: ["victorinox-werks-traveler-7-0-deluxe-briefcase"]
      },
      {
        heading: "5. American Tourister At Work Nxt 15.6\" – bästa datorväskan med hjul",
        handles: ["at-work-nxt-american-tourister-15-6-datorvaska-med-hjul"]
      },
      {
        heading: "6. Hedgren Display – bästa flexibla datorväskan",
        handles: ["hedgren-next-display-laptopvaska"]
      },
      {
        heading: "7. Hedgren Galao 14 tum – bästa kompakta datorväskan",
        handles: ["galao-hedgren-14-tum-datorvaska"]
      }
    ]
  };

  const isBestInTestArticle = () => {
    const { pathname } = window.location;
    return pathname.startsWith(BLOG_PREFIX) && pathname !== "/blogs/bast-i-test";
  };

  const getRootUrl = () => {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    return "/";
  };

  const getCurrencyCode = () => {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }

    return "SEK";
  };

  const normalizeText = (value) => {
    return (value || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\p{L}\p{N}]+/gu, " ")
      .trim()
      .toLowerCase();
  };

  const normalizeHeadingText = (value) => (value || "").replace(/\s+/g, " ").trim();

  const formatMoney = (amountInOre) => {
    const currency = getCurrencyCode();

    try {
      return new Intl.NumberFormat("sv-SE", {
        style: "currency",
        currency
      }).format((amountInOre || 0) / 100);
    } catch (error) {
      return `${((amountInOre || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const extractHandleFromLink = (href) => {
    if (!href) {
      return null;
    }

    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      return null;
    }
  };

  const extractRankedHeadingProductName = (headingText) => {
    if (!headingText) {
      return null;
    }

    const normalizedHeading = headingText.replace(/\s+/g, " ").trim();
    const match = normalizedHeading.match(/^\d+\.\s+(.+?)(?:\s+[–-]\s+.+)?$/);
    return match ? match[1].trim() : null;
  };

  const getProductImage = (product) => {
    if (typeof product.featured_image === "string" && product.featured_image) {
      return product.featured_image;
    }

    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images[0];
    }

    return "";
  };

  const findVariantToSell = (product) => {
    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) {
      return null;
    }

    return product.variants.find((variant) => variant.available) || product.variants[0];
  };

  const findInsertionTarget = (link) => {
    if (!link || link.closest("table")) {
      return null;
    }

    const block = link.closest("p, ul, ol, blockquote, h2, h3, h4, h5, h6");
    if (!block) {
      return null;
    }

    if (!/^H[2-6]$/.test(block.tagName)) {
      return block;
    }

    let candidate = block.nextElementSibling;
    let lastContentNode = block;

    while (candidate && !/^H[2-6]$/.test(candidate.tagName)) {
      if (
        candidate.matches("p, ul, ol, blockquote, div") &&
        candidate.textContent &&
        candidate.textContent.trim() !== ""
      ) {
        lastContentNode = candidate;
      }

      candidate = candidate.nextElementSibling;
    }

    return lastContentNode;
  };

  const findSectionEndTarget = (headingText, container) => {
    const headings = Array.from(container.querySelectorAll("h2"));
    const normalizedHeadingText = normalizeHeadingText(headingText);
    const startHeading = headings.find((heading) => normalizeHeadingText(heading.textContent) === normalizedHeadingText);

    if (!startHeading) {
      return null;
    }

    let candidate = startHeading.nextElementSibling;
    let lastContentNode = startHeading;

    while (candidate && candidate.tagName !== "H2") {
      if (candidate.textContent && candidate.textContent.trim() !== "") {
        lastContentNode = candidate;
      }

      candidate = candidate.nextElementSibling;
    }

    return lastContentNode;
  };

  const getUniqueProductHandles = (container) => {
    const entries = [];
    const seenTargets = new WeakSet();

    container.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const handle = extractHandleFromLink(link.getAttribute("href"));
      const target = findInsertionTarget(link);

      if (!handle || !target || seenTargets.has(target)) {
        return;
      }

      if (target.nextElementSibling && target.nextElementSibling.classList.contains(CARD_CLASS)) {
        seenTargets.add(target);
        return;
      }

      seenTargets.add(target);
      entries.push({ handle, target });
    });

    return entries;
  };

  const productCache = new Map();
  const searchCache = new Map();

  const fetchProduct = async (handle) => {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const request = fetch(`${getRootUrl()}products/${handle}.js`, {
      headers: { Accept: "application/json" }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load product ${handle}`);
        }

        return response.json();
      })
      .catch(() => null);

    productCache.set(handle, request);
    return request;
  };

  const fetchFirstAvailableProduct = async (handles) => {
    const handleList = Array.isArray(handles) ? handles : [handles];

    for (const handle of handleList) {
      const product = await fetchProduct(handle);
      if (product) {
        return product;
      }
    }

    return null;
  };

  const extractProductsMetaFromSearchHtml = (html) => {
    if (!html) {
      return [];
    }

    const match = html.match(/var meta = (\{"products":.*?"page":\{.*?\}\});/s);
    if (!match) {
      return [];
    }

    try {
      const parsed = JSON.parse(match[1]);
      return Array.isArray(parsed.products) ? parsed.products : [];
    } catch (error) {
      return [];
    }
  };

  const scoreProductMatch = (product, productName) => {
    const query = normalizeText(productName);
    const title = normalizeText(product && product.title);
    const handle = normalizeText(product && product.handle);

    if (!query || !title) {
      return -1;
    }

    let score = 0;

    if (title === query) {
      score += 1000;
    }

    if (title.includes(query)) {
      score += 400;
    }

    const queryTokens = query.split(" ").filter(Boolean);
    queryTokens.forEach((token) => {
      if (title.includes(token)) {
        score += token.length > 2 ? 40 : 10;
      }

      if (handle.includes(token)) {
        score += token.length > 2 ? 15 : 5;
      }
    });

    const titleTokens = new Set(title.split(" ").filter(Boolean));
    score -= Math.max(0, titleTokens.size - queryTokens.length) * 2;

    return score;
  };

  const searchProductHandleByName = async (productName) => {
    if (!productName) {
      return null;
    }

    if (searchCache.has(productName)) {
      return searchCache.get(productName);
    }

    const request = fetch(`${getRootUrl()}search?q=${encodeURIComponent(productName)}&type=product`, {
      headers: { Accept: "text/html" }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Search failed for ${productName}`);
        }

        return response.text();
      })
      .then((html) => {
        const products = extractProductsMetaFromSearchHtml(html);
        if (!products.length) {
          return null;
        }

        const ranked = products
          .map((product) => ({
            product,
            score: scoreProductMatch(product, productName)
          }))
          .sort((a, b) => b.score - a.score);

        return ranked[0] && ranked[0].score > 0 ? ranked[0].product.handle : null;
      })
      .catch(() => null);

    searchCache.set(productName, request);
    return request;
  };

  const refreshCartState = async () => {
    const response = await fetch(`${getRootUrl()}cart.js`, {
      headers: { Accept: "application/json" }
    });

    if (!response.ok) {
      throw new Error("Cart refresh failed");
    }

    const cart = await response.json();
    document.documentElement.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: { cart } }));
    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    return cart;
  };

  const addVariantToCart = async (variantId, quantity = 1) => {
    const response = await fetch(`${getRootUrl()}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({
        id: variantId,
        quantity
      })
    });

    if (!response.ok) {
      throw new Error("Cart add failed");
    }

    await refreshCartState();
  };

  const createCard = (product) => {
    const card = document.createElement("aside");
    card.className = CARD_CLASS;
    card.setAttribute("data-product-handle", product.handle);

    const variant = findVariantToSell(product);
    const image = getProductImage(product);
    const productUrl = product.url || `${getRootUrl()}products/${product.handle}`;
    const isAvailable = Boolean(variant && variant.available);
    const price = variant ? variant.price : product.price;
    const compareAtPrice = variant ? variant.compare_at_price : product.compare_at_price;

    card.innerHTML = `
      <div class="article-buy-card__media">
        <a href="${productUrl}" class="article-buy-card__image-link" aria-label="${product.title}">
          ${image ? `<img src="${image}" alt="${product.title}" class="article-buy-card__image" loading="lazy">` : ""}
        </a>
      </div>
      <div class="article-buy-card__content">
        ${product.vendor ? `<div class="article-buy-card__vendor heading heading--xsmall">${product.vendor}</div>` : ""}
        <a href="${productUrl}" class="article-buy-card__title">${product.title}</a>
        <div class="article-buy-card__price-list">
          <span class="article-buy-card__price">${formatMoney(price)}</span>
          ${compareAtPrice && compareAtPrice > price ? `<span class="article-buy-card__compare-price">${formatMoney(compareAtPrice)}</span>` : ""}
        </div>
        <div class="article-buy-card__actions">
          <a href="${productUrl}" class="button button--secondary">Visa produkt</a>
          <button type="button" class="button button--primary article-buy-card__add-button" ${isAvailable ? "" : "disabled"}>
            ${isAvailable ? "Lägg i kundvagn" : "Slutsåld"}
          </button>
        </div>
      </div>
    `;

    const addButton = card.querySelector(".article-buy-card__add-button");
    if (addButton && variant && isAvailable) {
      addButton.addEventListener("click", async () => {
        const originalText = addButton.textContent;
        addButton.disabled = true;
        addButton.textContent = "Lägger till...";

        try {
          await addVariantToCart(variant.id, 1);
          addButton.textContent = "Tillagd";
          window.setTimeout(() => {
            addButton.disabled = false;
            addButton.textContent = originalText;
          }, 1200);
        } catch (error) {
          addButton.disabled = false;
          addButton.textContent = "Försök igen";
          window.setTimeout(() => {
            addButton.textContent = originalText;
          }, 1400);
        }
      });
    }

    return card;
  };

  const injectArticleProductCards = async () => {
    if (!isBestInTestArticle()) {
      return;
    }

    const container = document.querySelector(ARTICLE_SELECTOR);
    if (!container || container.dataset.productCardsReady === "true") {
      return;
    }

    container.dataset.productCardsReady = "true";
    const mappedEntries = ARTICLE_PRODUCT_MAP[window.location.pathname] || [];

    for (const entry of mappedEntries) {
      const target = findSectionEndTarget(entry.heading, container);
      if (!target) {
        continue;
      }

      if (target.nextElementSibling && target.nextElementSibling.classList.contains(CARD_CLASS)) {
        continue;
      }

      const product = await fetchFirstAvailableProduct(entry.handles || entry.handle);
      if (!product) {
        continue;
      }

      const card = createCard(product);
      target.insertAdjacentElement("afterend", card);
    }

    const rankedHeadings = Array.from(container.querySelectorAll("h2"))
      .map((heading) => ({
        headingText: heading.textContent.trim(),
        productName: extractRankedHeadingProductName(heading.textContent)
      }))
      .filter((item) => item.productName);

    for (const item of rankedHeadings) {
      const target = findSectionEndTarget(item.headingText, container);
      if (!target) {
        continue;
      }

      if (target.nextElementSibling && target.nextElementSibling.classList.contains(CARD_CLASS)) {
        continue;
      }

      const handle = await searchProductHandleByName(item.productName);
      if (!handle) {
        continue;
      }

      const product = await fetchProduct(handle);
      if (!product) {
        continue;
      }

      const card = createCard(product);
      target.insertAdjacentElement("afterend", card);
    }

    const entries = getUniqueProductHandles(container);

    for (const entry of entries) {
      const product = await fetchProduct(entry.handle);
      if (!product) {
        continue;
      }

      const card = createCard(product);
      entry.target.insertAdjacentElement("afterend", card);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", injectArticleProductCards);
  } else {
    injectArticleProductCards();
  }
})();



















